import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Radio, Tooltip } from '@mui/material';
import { connect, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import _ from 'lodash';
import LabelValueDisplay from '../../components/LabelValue/LabelValueDisplay';
import DeleteWANProfileModal from '../../components/Modals/DeleteProfileModal';
import CommonModal from '../../components/Modals/CommonModal';
import BaseModal from '../../components/BaseModal/BaseModal';
import CustomLoader from '../../../../../components/Loader/CustomLoader';
import NetworkTable, { ColumnConfig, ColumnType } from '../../components/InterfacesTable/NetworkTable/NetworkTable';
import {
  getAllVpnProfilesData,
  getAddEditVpnProfileData,
  getDeleteVpnProfileData,
  setVpnProfileActiveData,
  getVpnProviderLoginData,
  getVpnProviderCountriesData,
  getVpnProviderServersData,
  getVpnProviderOvpnData,
  getVpnStatusData,
  getDeviceLinkStatusData,
} from '../../../../../actions/Users/authenticateOpenEdgeService';
import {
  GET_ADD_EDIT_VPN_PROFILE,
  GET_DELETE_VPN_PROFILE,
  SET_VPN_PROFILE_ACTIVE,
  GET_VPN_PROVIDER_LOGIN,
  GET_VPN_PROVIDER_OVPN,
} from '../../../../../actions/types';
import { getDataFromLocalStorage, formatBytes, convertSecondsToUptime } from '../../../../../utils/util';
import './VpnTab.css';

const radioSx = {
  p: 0,
  color: '#C2C7D4',
  '&.Mui-checked': { color: '#1B51A7' },
} as const;



interface Props {
  selectedRow?: any;
  refreshIconClicked?: any;
  notifyConfigAction?: () => void;
  notifyConfigActionComplete?: () => void;
  getAllVpnProfilesData: (deviceId: string) => void;
  getAddEditVpnProfileData: (payload: any, endPoint: string) => void;
  getDeleteVpnProfileData: (deviceId: string, id: number) => void;
  setVpnProfileActiveData: (deviceId: string, id: number, active: boolean) => void;
  getVpnProviderLoginData: (providerKey: string, payload: any) => void;
  getVpnProviderCountriesData: (providerKey: string, payload?: any) => void;
  getVpnProviderServersData: (providerKey: string, payload?: any) => void;
  getVpnProviderOvpnData: (providerKey: string, payload: any) => void;
  getVpnStatusData: (deviceId: string) => void;
  getDeviceLinkStatusData: (k4Id: string, deviceId: string) => void;
  getDeviceLinkStatus: any;
  deviceLinkStatusLoading: boolean;
  getVpnStatus: any;
  getVpnStatusLoading: boolean;
  getAllVpnProfiles: any;
  getAllVpnProfilesLoading: boolean;
  getAddEditVpnProfile: any;
  getAddEditVpnProfileLoading: boolean;
  getDeleteVpnProfile: any;
  getDeleteVpnProfileLoading: boolean;
  setVpnProfileActive: any;
  setVpnProfileActiveLoading: boolean;
  getVpnProviderLogin: any;
  getVpnProviderLoginLoading: boolean;
  getVpnProviderCountries: any;
  getVpnProviderCountriesLoading: boolean;
  getVpnProviderServers: any;
  getVpnProviderServersLoading: boolean;
  getVpnProviderOvpn: any;
  getVpnProviderOvpnLoading: boolean;
}

type StepKey = 'provider' | 'auth' | 'country' | 'server';

interface VpnProfile {
  id: number;
  DID: string;
  name: string;
  provider: string;
  hostname?: string;
  country?: string;
  protocol?: string;
  username?: string;
  password?: string;
  account_username?: string;
  account_password?: string;
  active?: boolean;
  enabled?: boolean;
  sync_status?: string;
  ovpn_config?: string;
  ovpn_filename?: string;
}

interface VpnSession {
  state?: string;
  interface?: string;
  local_ip?: string;
  gateway?: string;
  remote_ip?: string;
  rx_bytes?: number;
  tx_bytes?: number;
  uptime_seconds?: number;
  reconnect_attempts?: number;
  timestamp?: number;
  last_error?: string;
}

interface CountryOption {
  id: string;
  name: string;
  code: string;
  tier?: number;
}

interface ServerOption {
  key: string;
  label: string;
  location: string;
  load: number | null;
  udp: boolean;
  tcp: boolean;
  hostname: string;
  logicalId?: string;
  country?: string;
  // True only for the placeholder built from a profile's saved hostname when
  // that server wasn't present in the (limited/randomized) fetched list.
  synthetic?: boolean;
}

const STEP_ORDER: { key: StepKey; label: string }[] = [
  { key: 'provider', label: 'Provider' },
  { key: 'auth', label: 'Authentication' },
  { key: 'country', label: 'Country' },
  { key: 'server', label: 'Server' },
];

const NAME_MAX_LENGTH = 32;

const MAX_ACTIVE_PROFILES = 8;

const REFRESH_INTERVAL_MS = 30000;

interface ProviderMeta {
  id: string;
  name: string;
  endpointKey?: string;
  usernameLabel: string;
  passwordLabel: string;
  authNote?: string;
}

const PROVIDERS: ProviderMeta[] = [
  {
    id: 'manual',
    name: 'Manual',
    usernameLabel: 'Username',
    passwordLabel: 'Password',
  },
  {
    id: 'nordvpn',
    name: 'NordVPN',
    endpointKey: 'Nord',
    usernameLabel: 'Service Username',
    passwordLabel: 'Service Password',
    authNote: 'Use the NordVPN service credentials from your account dashboard.',
  },
  {
    id: 'protonvpn',
    name: 'ProtonVPN',
    endpointKey: 'Proton',
    usernameLabel: 'Proton Account Email',
    passwordLabel: 'Proton Account Password',
  },
];

const providerMeta = (id: string) => PROVIDERS.find((p) => p.id === id) ?? PROVIDERS[0];
const providerName = (id: string) => PROVIDERS.find((p) => p.id === id)?.name ?? id;

const loadColor = (load: number) => {
  if (load < 40) return '#22C55E';
  if (load < 70) return '#F59E0B';
  return '#EF4444';
};


const bytesOrDash = (bytes?: number) =>
  typeof bytes === 'number' && Number.isFinite(bytes) ? formatBytes(bytes) : '- -';

/** OpenVPN management-channel states (CONNECTING, RECONNECTING, AUTH_FAILED, …) into "Auth Failed" style labels. */
const formatTunnelState = (state?: string) => {
  if (!state) return '';
  return state
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const ERROR_TUNNEL_STATES = new Set(['exiting', 'auth_failed', 'failed', 'error']);

/** Non-printable separator used to smuggle a status row's error detail through
 * the table's status value so it can be shown as a tooltip on hover, without
 * needing NetworkTable's renderCell to pass the whole row. */
const STATUS_ERROR_SEP = '␟';

/** Dot color for a rendered status label — derived from the label itself since the table only hands renderCell the cell value. */
const statusTone = (status: string): 'connected' | 'error' | 'pending' | 'neutral' => {
  const normalized = status.trim().toLowerCase();
  if (normalized === 'not activated') return 'neutral';
  if (normalized === 'connected') return 'connected';
  if (ERROR_TUNNEL_STATES.has(normalized.replace(/ /g, '_'))) return 'error';
  return 'pending';
};

const REGION_NAMES = (() => {
  try {
    return new (Intl as any).DisplayNames(['en'], { type: 'region' });
  } catch {
    return null;
  }
})();

const countryNameFromCode = (code?: string) => {
  if (!code) return '';
  try {
    return REGION_NAMES?.of(code) ?? code;
  } catch {
    return code;
  }
};

const normaliseCountries = (raw: any): CountryOption[] => {
  if (!raw) return [];

  if (Array.isArray(raw)) {
    return raw.map((c: any) => ({
      id: String(c?.id ?? c?.code ?? ''),
      name: c?.name ?? countryNameFromCode(c?.code),
      code: c?.code ?? '',
    }));
  }

  const byCode = new Map<string, CountryOption>();
  Object.keys(raw).forEach((tierKey) => {
    const tier = Number(tierKey);
    const codes: string[] = raw[tierKey]?.CountryCodes ?? [];
    codes.forEach((code) => {
      const upper = String(code).toUpperCase();
      const existing = byCode.get(upper);
      if (existing && (existing.tier ?? Number.MAX_SAFE_INTEGER) <= tier) return;
      byCode.set(upper, {
        id: upper,
        name: countryNameFromCode(upper) || upper,
        code: upper,
        tier,
      });
    });
  });
  return Array.from(byCode.values());
};

const normaliseServers = (provider: string, raw: any): ServerOption[] => {
  if (!Array.isArray(raw)) return [];

  if (provider === 'protonvpn') {
    return raw.map((s: any) => {
      const logicalId = String(s?.id ?? '');
      const code = String(s?.country ?? '').toUpperCase();
      const domain = String(s?.domain ?? '');
      const load = s?.load;
      return {
        key: logicalId || domain,
        label: s?.name || domain || '- -',
        location: countryNameFromCode(code) || code || '- -',
        load: load === undefined || load === null ? null : Number(load),
        udp: true,
        tcp: true,
        hostname: domain || logicalId,
        logicalId: logicalId || undefined,
        country: code,
      };
    });
  }

  return raw.map((s: any) => ({
    key: String(s?.hostname ?? ''),
    label: s?.hostname ?? s?.name ?? '- -',
    location: s?.city ?? '- -',
    load: Number(s?.load ?? 0),
    udp: s?.udp !== false,
    tcp: !!s?.tcp,
    hostname: s?.hostname ?? '',
  }));
};

const OpenVpnConfig: React.FC<Props> = ({
  selectedRow,
  refreshIconClicked,
  notifyConfigAction,
  notifyConfigActionComplete,
  getAllVpnProfilesData,
  getAddEditVpnProfileData,
  getDeleteVpnProfileData,
  setVpnProfileActiveData,
  getVpnProviderLoginData,
  getVpnProviderCountriesData,
  getVpnProviderServersData,
  getVpnProviderOvpnData,
  getVpnStatusData,
  getDeviceLinkStatusData,
  getDeviceLinkStatus,
  deviceLinkStatusLoading,
  getVpnStatus,
  getVpnStatusLoading,
  getAllVpnProfiles,
  getAllVpnProfilesLoading,
  getAddEditVpnProfile,
  getAddEditVpnProfileLoading,
  getDeleteVpnProfile,
  getDeleteVpnProfileLoading,
  setVpnProfileActive,
  setVpnProfileActiveLoading,
  getVpnProviderLogin,
  getVpnProviderLoginLoading,
  getVpnProviderCountries,
  getVpnProviderCountriesLoading,
  getVpnProviderServers,
  getVpnProviderServersLoading,
  getVpnProviderOvpn,
  getVpnProviderOvpnLoading,
}) => {
  const dispatch = useDispatch();
  const deviceId = selectedRow?.deviceId;
  const k4Id = selectedRow?.k4Id;

  const [profiles, setProfiles] = useState<VpnProfile[]>([]);
  const [vpnSessions, setVpnSessions] = useState<Record<string, VpnSession>>({});
  const [editorOpen, setEditorOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [viewId, setViewId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<VpnProfile | null>(null);

  // ---- wizard state ----
  const [step, setStep] = useState<StepKey>('provider');
  const [profileName, setProfileName] = useState('');
  const [provider, setProvider] = useState('manual');

  const [serviceUsername, setServiceUsername] = useState('');
  const [servicePassword, setServicePassword] = useState('');
  const [ovpnFileName, setOvpnFileName] = useState('');
  const [ovpnConfig, setOvpnConfig] = useState('');
  const [viewFileOpen, setViewFileOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [session, setSession] = useState<{ accessToken?: string; uid?: string } | null>(null);
 
  const [tunnelCreds, setTunnelCreds] = useState<{ username: string; password: string } | null>(null);
  const [loginVerified, setLoginVerified] = useState(false);
  const [loginError, setLoginError] = useState('');

  const [country, setCountry] = useState('');
  const [countryLabel, setCountryLabel] = useState('');
  const [server, setServer] = useState('');
  const [serverProtocol, setServerProtocol] = useState('');

  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [servers, setServers] = useState<ServerOption[]>([]);

  /** Set while a login request raised by "Next" is in flight. */
  const pendingLoginRef = useRef(false);
  
  const pendingSaveRef = useRef<any>(null);
  const [saveError, setSaveError] = useState('');

  const meta = providerMeta(provider);
  const providerLabel = meta.name;
  const isManual = provider === 'manual';
  const isProton = provider === 'protonvpn';
  const hasProviderApi = !!meta.endpointKey;

  const wizardSteps = useMemo(
    () => (isManual ? STEP_ORDER.filter((s) => s.key === 'provider' || s.key === 'auth') : STEP_ORDER),
    [isManual]
  );

  const stepKeys = wizardSteps.map((s) => s.key);
  const lastStepKey = stepKeys[stepKeys.length - 1];
  const stepIndex = wizardSteps.findIndex((s) => s.key === step);

  const serverCountryCodes = useMemo(
    () => new Set(servers.map((s) => String(s.country ?? '').toUpperCase()).filter(Boolean)),
    [servers]
  );

  const countryHasServers = (code: string) =>
    !isProton || serverCountryCodes.size === 0 || serverCountryCodes.has(String(code).toUpperCase());


  const visibleServers = useMemo(() => {
    if (!isProton) return servers;
    if (!country) return [];
    const wanted = String(country).toUpperCase();
    return servers.filter((s) => String(s.country ?? '').toUpperCase() === wanted);
  }, [isProton, servers, country]);

  const selectedServer = useMemo(() => servers.find((s) => s.key === server), [servers, server]);

  // ============================ Data ============================

  // Set right before a background (interval) fetch so its loading flag does
  // not flash the full-screen loader; cleared once that fetch's data lands.
  const isBackgroundFetchRef = useRef(false);

  const fetchProfiles = (background = false) => {
    if (!deviceId) return;
    isBackgroundFetchRef.current = background;
    getAllVpnProfilesData(deviceId);
  };

  useEffect(() => {
    fetchProfiles();
  }, [deviceId, refreshIconClicked]);

  // Keep the profile list current without the user having to hit refresh, and
  // without showing the loader on every silent tick.
  useEffect(() => {
    if (!deviceId) return;
    const intervalId = setInterval(() => fetchProfiles(true), REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [deviceId]);

  // Interface online/offline status, same call every other tab makes on
  // manual refresh. Skip the very first firing (tab mount): InventoryPopup's
  // centralized poll already refreshes it once when this tab opens.
  const skipFirstLinkStatusFetchRef = useRef(true);
  useEffect(() => {
    if (!deviceId || !refreshIconClicked) return;
    if (skipFirstLinkStatusFetchRef.current) {
      skipFirstLinkStatusFetchRef.current = false;
    } else {
      getDeviceLinkStatusData(k4Id, deviceId);
    }
  }, [refreshIconClicked]);

  useEffect(() => {
    if (_.isEmpty(getAllVpnProfiles)) return;
    const rows: VpnProfile[] = getAllVpnProfiles?.vpn_profiles ?? [];
    setProfiles(rows);
    isBackgroundFetchRef.current = false;
  }, [getAllVpnProfiles]);

  // Live tunnel state, polled the same way as the profile list — same
  // background-fetch trick so silent ticks don't flash the full-screen loader.
  const isVpnStatusBackgroundFetchRef = useRef(false);

  const fetchVpnStatus = (background = false) => {
    if (!deviceId) return;
    isVpnStatusBackgroundFetchRef.current = background;
    getVpnStatusData(deviceId);
  };

  useEffect(() => {
    fetchVpnStatus();
  }, [deviceId, refreshIconClicked]);

  useEffect(() => {
    if (!deviceId) return;
    const intervalId = setInterval(() => fetchVpnStatus(true), REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [deviceId]);

  useEffect(() => {
    if (_.isEmpty(getVpnStatus)) return;
    setVpnSessions(getVpnStatus?.vpn_status?.sessions ?? {});
    isVpnStatusBackgroundFetchRef.current = false;
  }, [getVpnStatus]);

  // Refetch after any write; all of them resync the device.
  useEffect(() => {
    if (_.isEmpty(getAddEditVpnProfile)) return;
    notifyConfigActionComplete?.();
    if (getAddEditVpnProfile?.status) {
      setEditorOpen(false);
      resetWizard();
      fetchProfiles();
    } else {
      toast.error(
        getAddEditVpnProfile?.msg || getAddEditVpnProfile?.message || 'Failed to save OpenVPN profile.',
        { position: toast.POSITION.BOTTOM_LEFT }
      );
    }
    dispatch({ type: GET_ADD_EDIT_VPN_PROFILE, payload: {} });
  }, [getAddEditVpnProfile]);

  useEffect(() => {
    if (_.isEmpty(getDeleteVpnProfile)) return;
    notifyConfigActionComplete?.();
    if (getDeleteVpnProfile?.status) {
      fetchProfiles();
    } else {
      toast.error(
        getDeleteVpnProfile?.msg || getDeleteVpnProfile?.message || 'Failed to delete OpenVPN profile.',
        { position: toast.POSITION.BOTTOM_LEFT }
      );
    }
    dispatch({ type: GET_DELETE_VPN_PROFILE, payload: {} });
  }, [getDeleteVpnProfile]);

  useEffect(() => {
    if (_.isEmpty(setVpnProfileActive)) return;
    notifyConfigActionComplete?.();
    if (!setVpnProfileActive?.status) {
      toast.error(
        setVpnProfileActive?.msg || setVpnProfileActive?.message || 'Failed to update OpenVPN profile status.',
        { position: toast.POSITION.BOTTOM_LEFT }
      );
    }
    // Resync regardless of outcome so the toggle reflects the device's actual state.
    fetchProfiles();
    dispatch({ type: SET_VPN_PROFILE_ACTIVE, payload: {} });
  }, [setVpnProfileActive]);

  // ---- provider reference data ----

  useEffect(() => {
    if (_.isEmpty(getVpnProviderCountries)) return;
    setCountries(normaliseCountries(getVpnProviderCountries?.countries));
  }, [getVpnProviderCountries]);

  useEffect(() => {
    if (_.isEmpty(getVpnProviderServers)) return;
    const rawServers =
      getVpnProviderServers?.servers ??
      getVpnProviderServers?.logical_servers ??
      getVpnProviderServers?.LogicalServers;
    setServers(normaliseServers(provider, rawServers));
    const tunnelUsername = getVpnProviderServers?.tunnel_username;
    if (tunnelUsername) {
      setTunnelCreds({
        username: tunnelUsername,
        password: getVpnProviderServers?.tunnel_password ?? '',
      });
    }
  }, [getVpnProviderServers]);

  // ---- editing: the profile already has a country/server/protocol, so pick
  // them back up once the provider's lists load instead of asking the user
  // to reselect them. ----

  useEffect(() => {
    if (!editingId || country || countries.length === 0) return;
    const targetCountry = profileById(editingId)?.country;
    if (!targetCountry) return;
    const match = countries.find((c) => c.name.toLowerCase() === targetCountry.toLowerCase());
    if (!match) return;
    setCountry(match.id);
    setCountryLabel(match.name);
    // Proton's servers arrive alongside its countries; Nord's need this country's id.
    if (!isProton) {
      getVpnProviderServersData(meta.endpointKey as string, { country_id: Number(match.id), limit: 20 });
    }
  }, [countries, editingId]);

  useEffect(() => {
    if (!editingId || server || servers.length === 0) return;
    const p = profileById(editingId);
    if (!p?.hostname) return;
    const match = servers.find((s) => s.hostname === p.hostname);
    if (match) {
      setServer(match.key);
      setServerProtocol(p.protocol || (match.udp ? 'udp' : 'tcp'));
      return;
    }
    // The saved server wasn't in this (limited/randomized) batch fetched for the
    // country - synthesize an entry from the profile itself so it still shows as
    // selected instead of silently dropping the previously configured server.
    const synthetic: ServerOption = {
      key: p.hostname,
      label: p.hostname,
      location: countryLabel,
      load: null,
      udp: p.protocol === 'udp',
      tcp: p.protocol !== 'udp',
      hostname: p.hostname,
      synthetic: true,
    };
    setServers((prev) => [synthetic, ...prev]);
    setServer(synthetic.key);
    setServerProtocol(p.protocol || (synthetic.udp ? 'udp' : 'tcp'));
  }, [servers, editingId]);

  // ---- login lands -> open the country step ----

  useEffect(() => {
    if (_.isEmpty(getVpnProviderLogin) || !pendingLoginRef.current) return;
    pendingLoginRef.current = false;

    const res = getVpnProviderLogin;
    dispatch({ type: GET_VPN_PROVIDER_LOGIN, payload: {} });

    if (!res?.status) {
      setLoginError(res?.msg || res?.message || `${providerLabel} did not accept these credentials.`);
      return;
    }

    const nextSession = isProton ? { accessToken: res?.accessToken, uid: res?.uid } : null;
    setLoginError('');
    setLoginVerified(true);
    setSession(nextSession);
    loadCountryStep(nextSession);
    setStep('country');
  }, [getVpnProviderLogin]);

  // ---- ovpn config lands -> the parked save goes out ----

  useEffect(() => {
    if (_.isEmpty(getVpnProviderOvpn) || !pendingSaveRef.current) return;
    const payload = pendingSaveRef.current;
    pendingSaveRef.current = null;

    const res = getVpnProviderOvpn;
    dispatch({ type: GET_VPN_PROVIDER_OVPN, payload: {} });

    const config = res?.ovpn_config ?? res?.config ?? res?.ovpn ?? '';
    if (!config) {
      setSaveError(
        res?.msg || res?.message || `${providerLabel} did not return an OpenVPN configuration. Try again.`
      );
      // The save never reaches getAddEditVpnProfileData on this path, so that
      // effect's notifyConfigActionComplete() will never fire - stop here instead.
      notifyConfigActionComplete?.();
      return;
    }

    getAddEditVpnProfileData(
      { ...payload, ovpn_config: config },
      payload?.id !== undefined ? 'updateVpnProfile' : 'addVpnProfile'
    );
  }, [getVpnProviderOvpn]);

  // ============================ Actions ============================

  /** Fetches whatever the Country step needs for the current provider. */
  const loadCountryStep = (activeSession: { accessToken?: string; uid?: string } | null) => {
    const key = meta.endpointKey;
    if (!key) return;

    if (isProton) {
      const body = { accessToken: activeSession?.accessToken, uid: activeSession?.uid };
      getVpnProviderCountriesData(key, body);
      getVpnProviderServersData(key, body);
      return;
    }

    getVpnProviderCountriesData(key, {});
  };

  const resetWizard = () => {
    setProfileName('');
    setProvider('manual');
    setServiceUsername('');
    setServicePassword('');
    setOvpnFileName('');
    setOvpnConfig('');
    setViewFileOpen(false);
    setSession(null);
    setTunnelCreds(null);
    setLoginVerified(false);
    setLoginError('');
    setCountry('');
    setCountryLabel('');
    setServer('');
    setServerProtocol('');
    setCountries([]);
    setServers([]);
    setStep('provider');
    setEditingId(null);
    setSaveError('');
    pendingLoginRef.current = false;
    pendingSaveRef.current = null;
  };

  const invalidateLogin = () => {
    setLoginVerified(false);
    setLoginError('');
    setSession(null);
    setTunnelCreds(null);
    setSaveError('');
  };

  const handleAdd = () => {
    resetWizard();
    setEditorOpen(true);
  };

  const handleEdit = (p: VpnProfile) => {
    resetWizard();
    setEditingId(p.id);
    setProfileName(p.name);
    setProvider(p.provider);
    // Proton's Authentication step edits the account email/password, not the
    // tunnel credentials stored in username/password — see submitProfile.
    setServiceUsername((p.provider === 'protonvpn' ? p.account_username : p.username) ?? '');
    setServicePassword((p.provider === 'protonvpn' ? p.account_password : p.password) ?? '');
    setOvpnFileName(p.ovpn_filename ?? '');
    setOvpnConfig(p.ovpn_config ?? '');
    // Real ids (country code / server key) are filled in once the provider's
    // country/server lists load — see the auto-select effects above.
    setCountryLabel(p.country ?? '');
    setServerProtocol(p.protocol ?? '');
    setDetailsOpen(false);
    setEditorOpen(true);
  };

  const handleViewDetails = (p: VpnProfile) => {
    setViewId(p.id);
    setDetailsOpen(true);
  };

  const profileById = (id?: number | null) => profiles.find((p) => p.id === id);

  const confirmDelete = () => {
    if (deleteTarget && deviceId) {
      notifyConfigAction?.();
      getDeleteVpnProfileData(deviceId, deleteTarget.id);
    }
    setDeleteTarget(null);
  };

  /** Number of profiles currently marked active. */
  const activeCount = useMemo(() => profiles.filter((p) => p.active).length, [profiles]);

  const handleActivate = (id: number) => {
    if (!deviceId) return;
    const alreadyActive = profiles.find((p) => p.id === id)?.active;
    if (!alreadyActive && activeCount >= MAX_ACTIVE_PROFILES) {
      toast.error(`Only ${MAX_ACTIVE_PROFILES} OpenVPN profiles can be active at a time.`, {
        position: toast.POSITION.BOTTOM_LEFT,
      });
      return;
    }
    notifyConfigAction?.();
    setVpnProfileActiveData(deviceId, id, true);
  };

  const handleDeactivate = (id: number) => {
    if (deviceId) {
      notifyConfigAction?.();
      setVpnProfileActiveData(deviceId, id, false);
    }
  };


  const submitProfile = () => {
    // For every provider except Proton, the profile's stored username/password
    // is whatever was used to log in during the Authentication step. Proton is
    // different: username/password must be the OpenVPN tunnel credentials
    // (tunnel_username / tunnel_password from vpnProtonServers) since those are
    // what the tunnel actually authenticates with, while the Proton account
    // email/password used to log in are kept separately as account_username /
    // account_password.
    const payload: any = {
      DID: deviceId,
      name: profileName.trim(),
      provider,
      username: isProton ? tunnelCreds?.username ?? serviceUsername : serviceUsername,
      password: isProton ? tunnelCreds?.password ?? servicePassword : servicePassword,
      userName: getDataFromLocalStorage('loggedInEmail'),
      k4Id: k4Id,
    };

    if (isProton) {
      payload.account_username = serviceUsername;
      payload.account_password = servicePassword;
    }

    if (editingId !== null) payload.id = editingId;

    if (isManual) {
      payload.ovpn_config = ovpnConfig;
      payload.ovpn_filename = ovpnFileName;
      notifyConfigAction?.();
      getAddEditVpnProfileData(payload, editingId !== null ? 'updateVpnProfile' : 'addVpnProfile');
      return;
    }

    const hostname = selectedServer?.hostname || server;
    payload.hostname = hostname;
    payload.country = countryLabel;
    payload.protocol = serverProtocol || 'udp';

    setSaveError('');

    
    if (isProton) {
      const logicalId = selectedServer?.logicalId || server;
      if (!session?.accessToken || !session?.uid || !logicalId) {
        setSaveError(
          'This ProtonVPN session is no longer usable. Go back, sign in again and re-select the server.'
        );
        return;
      }
      pendingSaveRef.current = payload;
      notifyConfigAction?.();
      getVpnProviderOvpnData(meta.endpointKey as string, {
        accessToken: session.accessToken,
        uid: session.uid,
        logicalId,
        protocol: payload.protocol,
      });
      return;
    }

    if (!hostname) {
      setSaveError('Select a server before saving.');
      return;
    }
    pendingSaveRef.current = payload;
    notifyConfigAction?.();
    getVpnProviderOvpnData(meta.endpointKey as string, {
      hostname,
      protocol: payload.protocol,
    });
  };

  // ---- wizard validation ----

  const isDuplicateName = () =>
    profiles.some(
      (p) => p.id !== editingId && (p.name ?? '').toLowerCase() === profileName.trim().toLowerCase()
    );

  const authValid = useMemo(() => {
    if (!serviceUsername.trim() || !servicePassword) return false;
    return isManual ? !!ovpnConfig : true;
  }, [isManual, serviceUsername, servicePassword, ovpnConfig]);

  const canProceed = () => {
    switch (step) {
      case 'provider':
        return !!provider && !!profileName.trim() && !isDuplicateName();
      case 'auth':
        return authValid;
      case 'country':
        return !!country;
      case 'server':
        if (isProton && !tunnelCreds) return false;
        return !!server;
      default:
        return true;
    }
  };

  const goNext = () => {
    const idx = stepKeys.indexOf(step);
    if (idx === -1 || idx >= stepKeys.length - 1) return;
    const next = stepKeys[idx + 1];

    // Leaving Authentication on a provider flow: verify the credentials first.
    // The response advances the wizard, since Proton's session arrives with it.
    if (step === 'auth' && hasProviderApi && !loginVerified) {
      pendingLoginRef.current = true;
      setLoginError('');
      getVpnProviderLoginData(meta.endpointKey as string, {
        username: serviceUsername,
        password: servicePassword,
      });
      return;
    }

    if (next === 'country' && hasProviderApi) loadCountryStep(session);
    setStep(next);
  };

  const goBack = () => {
    const idx = stepKeys.indexOf(step);
    if (idx > 0) setStep(stepKeys[idx - 1]);
  };

  const selectServer = (s?: ServerOption) => {
    if (!s) return;
    setServer(s.key);
    setServerProtocol(s.udp ? 'udp' : 'tcp');
    setSaveError('');
  };

  // ============================ Renderers ============================

  const tableRows = profiles.map((p) => {
    const session = vpnSessions[String(p.id)];
    const tunnelState = p.active ? formatTunnelState(session?.state) : '';
    const statusText = p.active ? tunnelState || 'Connecting' : 'Not Activated';
    const statusError = p.active && statusTone(statusText) === 'error' ? session?.last_error : undefined;
    const hasTransferData = p.active && (session?.rx_bytes !== undefined || session?.tx_bytes !== undefined);
    return {
      id: String(p.id),
      enabled: !!p.active,
      name: p.name,
      profile_name: providerName(p.provider),
      network: p.country || '--',
      status: statusError ? `${statusText}${STATUS_ERROR_SEP}${statusError}` : statusText,
      ip_address: (p.active && session?.local_ip) || '--',
      time: (p.active && convertSecondsToUptime(session?.uptime_seconds)) || '--',
      usage_total: hasTransferData ? `${bytesOrDash(session?.rx_bytes)} / ${bytesOrDash(session?.tx_bytes)}` : '--',
    };
  });

  const tableColumns: ColumnConfig[] = [
    { label: 'Enable/Disable', key: 'enabled', type: ColumnType.sliderText, maxWidth: '110px' },
    { label: 'Profile', key: 'name', type: ColumnType.hyperlinkText, maxWidth: '180px' },
    { label: 'Provider', key: 'profile_name', type: ColumnType.text, maxWidth: '120px' },
    { label: 'Country', key: 'network', type: ColumnType.text, maxWidth: '120px' },
    {
      label: 'State',
      key: 'status',
      type: ColumnType.text,
      maxWidth: '140px',
      renderCell: (value) => {
        const [text, errorDetail] = String(value).split(STATUS_ERROR_SEP);
        const content = (
          <span className="vpn-status-inline">
            <span className={`vpn-status-dot vpn-status-dot--${statusTone(text)}`} />
            {text}
          </span>
        );
        return errorDetail ? (
          <Tooltip title={errorDetail} arrow>
            {content}
          </Tooltip>
        ) : (
          content
        );
      },
    },
    { label: 'Local IP', key: 'ip_address', type: ColumnType.text, maxWidth: '120px' },
    { label: 'Uptime', key: 'time', type: ColumnType.text, maxWidth: '120px' },
    { label: 'RX / TX', key: 'usage_total', type: ColumnType.text, maxWidth: '160px' },
    {
      label: 'Actions',
      key: 'custom',
      type: ColumnType.actions,
      actions: () => ['setting', 'delete'],
    },
  ];

  const renderList = () => (
    <div className="vpn-openvpn-root">
      <NetworkTable
        data={tableRows as any}
        columns={tableColumns}
        showFilterBar
        addButtonLabel="Add OpenVPN Profile"
        onAddClick={handleAdd}
        onToggle={(_key: string, checked: boolean, row: any) =>
          checked ? handleActivate(Number(row?.id)) : handleDeactivate(Number(row?.id))
        }
        onHyperlinkClick={(_key: string, _value: string, row?: any) => {
          const p = profileById(Number(row?.id));
          if (p) handleViewDetails(p);
        }}
        onActionClick={(type: string, _key: string, row: any) => {
          const p = profileById(Number(row?.id));
          if (!p) return;
          if (type === 'setting') handleEdit(p);
          else if (type === 'delete') setDeleteTarget(p);
        }}
      />
    </div>
  );

  const renderSteps = () => (
    <div className="vpn-steps">
      {wizardSteps.map((s, i) => {
        const state = i === stepIndex ? 'active' : i < stepIndex ? 'done' : 'todo';
        return (
          <React.Fragment key={s.key}>
            <div className={`vpn-step vpn-step--${state}`}>
              <span className="vpn-step-num">{i + 1}</span>
              <span className="vpn-step-label">{s.label}</span>
            </div>
            {i < wizardSteps.length - 1 && <span className="vpn-step-divider" />}
          </React.Fragment>
        );
      })}
    </div>
  );

  const renderProvider = () => (
    <div className="vpn-body">
      <div className="vpn-form-grid">
        <LabelValueDisplay
          label="Profile Name"
          isInput
          value={profileName}
          onChange={(e: any) => setProfileName(String(e.target.value).slice(0, NAME_MAX_LENGTH))}
          placeholder="e.g. HQ - US East"
          error={profileName.trim() && isDuplicateName() ? 'Profile Name should be Unique.' : ''}
        />
        <LabelValueDisplay
          label="Service Provider"
          isDropdown
          value={providerLabel}
          dropdownOptions={PROVIDERS.map((p) => p.name)}
          onChange={(e: any) => {
            setProvider(PROVIDERS.find((p) => p.name === e.target.value)?.id ?? 'manual');
            setServer('');
            setCountry('');
            setCountryLabel('');
            setServers([]);
            setCountries([]);
            invalidateLogin();
          }}
          placeholder="Select provider"
          disabled={editingId !== null}
        />
      </div>
    </div>
  );

  const renderAuth = () => (
    <div className="vpn-body">
      <p className="vpn-section-title">{providerLabel} Authentication</p>

      {hasProviderApi && (
        <>
          <div className="vpn-form-grid">
            <LabelValueDisplay
              label={meta.usernameLabel}
              isInput
              value={serviceUsername}
              onChange={(e: any) => {
                setServiceUsername(e.target.value);
                invalidateLogin();
              }}
              placeholder={`Enter ${meta.usernameLabel.toLowerCase()}`}
            />
            <LabelValueDisplay
              label={meta.passwordLabel}
              isPassword
              boxWidth={300}
              value={servicePassword}
              onChange={(e: any) => {
                setServicePassword(e.target.value);
                invalidateLogin();
              }}
              placeholder={`Enter ${meta.passwordLabel.toLowerCase()}`}
            />
          </div>
          {loginError && (
            <p className="vpn-error-text" style={{ marginTop: 14 }}>
              {loginError}
            </p>
          )}
          {meta.authNote && (
            <p className="vpn-section-sub" style={{ marginTop: 14 }}>
              {meta.authNote}
            </p>
          )}
        </>
      )}

      {isManual && (
        <div className="vpn-form-grid">
          <div className="vpn-form-full">
            <div
              className="label-text"
              style={{ color: '#6E7079', marginBottom: '6px', fontSize: '12px', fontFamily: 'Inter' }}
            >
              Upload .ovpn File
            </div>
            <div className="vpn-upload">
              <button className="vpn-upload-btn" onClick={() => fileInputRef.current?.click()}>
                ⬆ Choose .ovpn file
              </button>
              {ovpnFileName ? (
                <>
                  <span className="vpn-upload-filename">{ovpnFileName}</span>
                  {ovpnConfig && (
                    <button
                      type="button"
                      className="vpn-upload-view-link"
                      onClick={() => setViewFileOpen(true)}
                    >
                      View File
                    </button>
                  )}
                </>
              ) : (
                <span className="vpn-upload-empty">No file selected</span>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".ovpn"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setOvpnFileName(file.name);
                  // The file's text is what the backend stores as ovpn_config.
                  const reader = new FileReader();
                  reader.onload = () => setOvpnConfig(String(reader.result ?? ''));
                  reader.readAsText(file);
                }}
              />
            </div>
          </div>
          <LabelValueDisplay
            label={meta.usernameLabel}
            isInput
            value={serviceUsername}
            onChange={(e: any) => setServiceUsername(e.target.value)}
            placeholder="Enter username"
          />
          <LabelValueDisplay
            label={meta.passwordLabel}
            isPassword
            boxWidth={300}
            value={servicePassword}
            onChange={(e: any) => setServicePassword(e.target.value)}
            placeholder="Enter password"
          />
        </div>
      )}
    </div>
  );

  const renderCountryItem = (c: CountryOption) => {
    const hasServers = countryHasServers(c.id);
    return (
      <div
        key={c.id}
        className={[
          'vpn-country-item',
          String(country) === String(c.id) ? 'vpn-country-item--selected' : '',
          hasServers ? '' : 'vpn-country-item--empty',
        ]
          .filter(Boolean)
          .join(' ')}
        title={hasServers ? undefined : 'No servers available on this account'}
        onClick={() => {
          setCountry(String(c.id));
          setCountryLabel(c.name);
          setServer('');
          setServerProtocol('');
          // Proton's list is already loaded and filtered client-side.
          if (!isProton) {
            getVpnProviderServersData(meta.endpointKey as string, {
              country_id: Number(c.id),
              limit: 20,
            });
          }
        }}
      >
        <div className="vpn-country-left">
          <span className="vpn-country-code">{c.code || '--'}</span>
          <span className="vpn-country-name">{c.name}</span>
        </div>
      </div>
    );
  };

  const renderCountry = () => {
    // The already-selected country (set on entry when editing) leads the list
    // so it doesn't get lost among however many others the provider returns.
    const sorted = [...countries].sort((a, b) => {
      if (String(a.id) === String(country)) return -1;
      if (String(b.id) === String(country)) return 1;
      return String(a?.name).localeCompare(String(b?.name));
    });
    const withServers = sorted.filter((c) => countryHasServers(c.id));
    const withoutServers = sorted.filter((c) => !countryHasServers(c.id));

    return (
      <div className="vpn-body">
        <p className="vpn-section-title">Select Country</p>
        <p className="vpn-section-sub">Countries available for {providerLabel}.</p>
        {countries.length === 0 && !getVpnProviderCountriesLoading && (
          <p className="vpn-section-sub">No countries returned by the provider.</p>
        )}

        <div className="vpn-country-grid">{withServers.map(renderCountryItem)}</div>

        {withoutServers.length > 0 && (
          <div className="vpn-country-section">
            <div className="vpn-country-section-head">
              <span className="vpn-country-section-title">
                No servers available ({withoutServers.length})
              </span>
              <span className="vpn-country-section-sub">
                {providerLabel} lists these countries, but none of their servers are available on
                this account.
              </span>
            </div>
            <div className="vpn-country-grid">{withoutServers.map(renderCountryItem)}</div>
          </div>
        )}
      </div>
    );
  };

  const renderServer = () => {
    const editingProfile = editingId != null ? profileById(editingId) : undefined;
    // While editing, the selected server always gets its own row above the
    // rest of the list - whether it was actually present in the fetched
    // batch or had to be synthesized because it wasn't - instead of just
    // sorting to the top of a single mixed list.
    const currentServer =
      editingProfile && server ? visibleServers.find((s) => s.key === server) : undefined;
    const otherServers = currentServer
      ? visibleServers.filter((s) => s.key !== currentServer.key)
      : visibleServers;
    const isOriginalServer = !!currentServer && editingProfile?.hostname === currentServer.hostname;
    const findServer = (key: string) => visibleServers.find((s) => s.key === key);

    // Outside the edit split above (e.g. the add-profile flow), the
    // already-selected server still leads the single list so it doesn't get
    // lost among however many others the provider returns.
    const orderedServers =
      !currentServer && server
        ? [...otherServers].sort((a, b) => (a.key === server ? -1 : b.key === server ? 1 : 0))
        : otherServers;
    const showLoad = otherServers.some((s) => s.load !== null && Number.isFinite(s.load));

    // Selection is driven by the radio or the server-name link.
    const toRow = (s: ServerOption) => ({
      id: s.key,
      name: s.label,
      network: s.location,
      status: s.load,
      link_type: [s.udp ? 'UDP' : '', s.tcp ? 'TCP' : ''].filter(Boolean).join(','),
      activeBg: server === s.key ? '#F5F8FE' : undefined,
    });
    const serverRows = orderedServers.map(toRow);
    // The top row shows the protocol actually in use on this profile, not
    // everything the server happens to support.
    const currentServerRows = currentServer
      ? [{ ...toRow(currentServer), link_type: (serverProtocol || 'udp').toUpperCase() }]
      : [];

    const radioColumn: ColumnConfig = {
      label: '',
      key: 'id',
      type: ColumnType.text,
      maxWidth: '44px',
      renderCell: (value) => (
        <Radio
          checked={server === value}
          size="small"
          sx={radioSx}
          onClick={() => selectServer(findServer(value))}
        />
      ),
    };
    const nameColumn: ColumnConfig = {
      label: 'Server Name',
      key: 'name',
      type: ColumnType.hyperlinkText,
      maxWidth: '220px',
    };
    const cityColumn: ColumnConfig = {
      label: isProton ? 'Country' : 'City',
      key: 'network',
      type: ColumnType.text,
      maxWidth: '160px',
    };
    const loadColumn: ColumnConfig = {
      label: 'Load %',
      key: 'status',
      type: ColumnType.text,
      maxWidth: '200px',
      renderCell: (value) => {
        const load = Number(value);
        // A single server missing its load keeps the column but not a 0% bar.
        if (value === null || value === undefined || value === '' || !Number.isFinite(load))
          return <span>- -</span>;
        return (
          <div className="vpn-load">
            <div className="vpn-load-bar">
              <div
                className="vpn-load-fill"
                style={{ width: `${load}%`, background: loadColor(load) }}
              />
            </div>
            <span className="vpn-load-pct" style={{ color: loadColor(load) }}>
              {load}%
            </span>
          </div>
        );
      },
    };
    const protocolColumn: ColumnConfig = {
      label: 'Protocol',
      key: 'link_type',
      type: ColumnType.text,
      maxWidth: '140px',
      renderCell: (value) => (
        <span className="vpn-status-inline">
          {String(value)
            .split(',')
            .filter(Boolean)
            .map((p) => (
              <span key={p} className={`vpn-badge vpn-badge--${p.toLowerCase()}`}>
                {p}
              </span>
            ))}
        </span>
      ),
    };

    // The selected-server row never shows a Load % column - only the rest
    // of the list decides whether that column exists at all.
    const currentServerColumns: ColumnConfig[] = [radioColumn, nameColumn, cityColumn, protocolColumn];
    const serverColumns: ColumnConfig[] = [
      radioColumn,
      nameColumn,
      cityColumn,
      ...(showLoad ? [loadColumn] : []),
      protocolColumn,
    ];

    const protocolOptions = [
      ...(selectedServer?.udp ? ['UDP'] : []),
      ...(selectedServer?.tcp ? ['TCP'] : []),
    ];

    return (
      <div className="vpn-body">
        <p className="vpn-section-title">Select Server</p>
        <p className="vpn-section-sub">
          {showLoad
            ? 'Lower load generally means better performance.'
            : `Servers available for ${providerLabel}.`}
        </p>

        {currentServer && (
          <>
            <p className="vpn-section-sub" style={{ fontWeight: 600, marginBottom: 6 }}>
              {isOriginalServer ? 'Currently configured server' : 'Selected server'}
            </p>
            <NetworkTable
              data={currentServerRows as any}
              columns={currentServerColumns}
              showFilterBar={false}
              onActionClick={() => {}}
              onHyperlinkClick={(_key: string, _value: string, row?: any) =>
                selectServer(findServer(row?.id))
              }
            />
            {otherServers.length > 0 && (
              <p className="vpn-section-sub" style={{ fontWeight: 600, marginTop: 20, marginBottom: 6 }}>
                Select other server
              </p>
            )}
          </>
        )}

        {otherServers.length === 0 && !getVpnProviderServersLoading ? (
          !currentServer && (
            <p className="vpn-section-sub">
              {servers.length > 0 && countryLabel
                ? `No servers available in ${countryLabel}.`
                : 'No servers returned by the provider.'}
            </p>
          )
        ) : (
          <NetworkTable
            data={serverRows as any}
            columns={serverColumns}
            showFilterBar={false}
            onActionClick={() => {}}
            onHyperlinkClick={(_key: string, _value: string, row?: any) =>
              selectServer(findServer(row?.id))
            }
          />
        )}

        {/* Save is blocked in canProceed without these — say why. */}
        {isProton && !tunnelCreds && !getVpnProviderServersLoading && (
          <p className="vpn-error-text" style={{ marginTop: 14 }}>
            Proton did not return OpenVPN tunnel credentials for this account. Go back and sign in
            again — the profile cannot be saved without them.
          </p>
        )}

        {saveError && (
          <p className="vpn-error-text" style={{ marginTop: 14 }}>
            {saveError}
          </p>
        )}

        {selectedServer && protocolOptions.length > 1 && (
          <div className="vpn-form-grid" style={{ marginTop: 16 }}>
            <LabelValueDisplay
              label="Protocol"
              isDropdown
              value={(serverProtocol || 'udp').toUpperCase()}
              dropdownOptions={protocolOptions}
              onChange={(e: any) => setServerProtocol(String(e.target.value).toLowerCase())}
            />
          </div>
        )}
      </div>
    );
  };

  const renderEditorBody = () => {
    const bodyByStep: Record<StepKey, () => JSX.Element> = {
      provider: renderProvider,
      auth: renderAuth,
      country: renderCountry,
      server: renderServer,
    };
    return (
      <div className="vpn-openvpn-root vpn-editor-body">
        {renderSteps()}
        {bodyByStep[step]()}
      </div>
    );
  };

 
  const renderDetailsBody = () => {
    const p = profileById(viewId);
    if (!p) return null;
    const active = !!p.active;
    const session = vpnSessions[String(p.id)];
    // Manual profiles have no country: their server comes from inside the
    // uploaded .ovpn, so nothing ever sets one.
    const isManualProfile = p.provider === 'manual';

    return (
      <div className="vpn-openvpn-root vpn-editor-body">
        <div className={`vpn-status-banner ${active ? 'vpn-status-banner--connected' : 'vpn-status-banner--error'}`}>
          <span className={`vpn-status-dot ${active ? 'vpn-status-dot--connected' : 'vpn-status-dot--error'}`} />
          <span className="vpn-status-banner-title">{active ? 'Active' : 'Inactive'}</span>
          <span className="vpn-status-banner-sub">OpenVPN · {providerName(p.provider)}</span>
        </div>

        <div className="vpn-status-grid">
          <LabelValueDisplay label="Provider" value={providerName(p.provider)} />
          <LabelValueDisplay label="Server" value={p.hostname || '- -'} />
          {!isManualProfile && <LabelValueDisplay label="Country" value={p.country || '- -'} />}
          <LabelValueDisplay label="Protocol" value={(p.protocol || '- -').toUpperCase()} />
          <LabelValueDisplay label="Username" value={p.username || '- -'} />
        </div>

        {active && (
          <>
            <p className="vpn-section-title" style={{ marginTop: 20 }}>
              Live Tunnel
            </p>
            {session ? (
              <div className="vpn-status-grid">
                <LabelValueDisplay
                  label="Tunnel State"
                  value={
                    statusTone(formatTunnelState(session.state)) === 'error' && session.last_error ? (
                      <>
                        {formatTunnelState(session.state)}{' '}
                        <span className="vpn-error-text">({session.last_error})</span>
                      </>
                    ) : (
                      formatTunnelState(session.state) || '- -'
                    )
                  }
                />
                <LabelValueDisplay label="Interface" value={session.interface || '- -'} />
                <LabelValueDisplay label="Local IP" value={session.local_ip || '- -'} />
                <LabelValueDisplay label="Remote IP" value={session.remote_ip || session.gateway || '- -'} />
                <LabelValueDisplay
                  label="Uptime"
                  value={convertSecondsToUptime(session.uptime_seconds) || '- -'}
                />
                <LabelValueDisplay
                  label="Data Transferred"
                  value={
                    session.rx_bytes === undefined && session.tx_bytes === undefined
                      ? '- -'
                      : `↓ ${bytesOrDash(session.rx_bytes)} · ↑ ${bytesOrDash(session.tx_bytes)}`
                  }
                />
              </div>
            ) : null}
            {!session && (
              <p className="vpn-section-sub">
                No live tunnel data yet — this refreshes every {REFRESH_INTERVAL_MS / 1000}s.
              </p>
            )}
          </>
        )}

        <div className="vpn-actions">
          {active ? (
            <button className="vpn-btn vpn-btn--danger" onClick={() => handleDeactivate(p.id)}>
              Deactivate
            </button>
          ) : (
            <button
              className="vpn-btn vpn-btn--primary"
              onClick={() => handleActivate(p.id)}
              disabled={activeCount >= MAX_ACTIVE_PROFILES}
              title={
                activeCount >= MAX_ACTIVE_PROFILES
                  ? `Only ${MAX_ACTIVE_PROFILES} OpenVPN profiles can be active at a time.`
                  : undefined
              }
            >
              Activate
            </button>
          )}
        </div>
        {!active && activeCount >= MAX_ACTIVE_PROFILES && (
          <p className="vpn-error-text" style={{ marginTop: 8 }}>
            Only {MAX_ACTIVE_PROFILES} OpenVPN profiles can be active at a time. Deactivate another
            profile first.
          </p>
        )}
      </div>
    );
  };

  const detailsProfile = profileById(viewId);
  const anyLoading =
    (getAllVpnProfilesLoading && !isBackgroundFetchRef.current) ||
    (getVpnStatusLoading && !isVpnStatusBackgroundFetchRef.current) ||
    getAddEditVpnProfileLoading ||
    getDeleteVpnProfileLoading ||
    setVpnProfileActiveLoading ||
    getVpnProviderLoginLoading ||
    getVpnProviderOvpnLoading;

  return (
    <>
      {renderList()}

      <CommonModal
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          resetWizard();
        }}
        onSave={() => (step === lastStepKey ? submitProfile() : goNext())}
        onCancelSave={goBack}
        title={editingId !== null ? 'Edit OpenVPN Profile' : 'Add OpenVPN Profile'}
        saveText={step === lastStepKey ? 'Configure OpenVPN' : 'Next'}
        cancelText={stepIndex === 0 ? 'Cancel' : 'Back'}
        disableSave={!canProceed() || anyLoading}
        showOnlineOffline={false}
        showSyncStatus={false}
        width="820px"
        maxWidth="820px"
        height="auto"
      >
        {renderEditorBody()}
      </CommonModal>

      <BaseModal
        open={viewFileOpen}
        onClose={() => setViewFileOpen(false)}
        title={ovpnFileName || 'OpenVPN File'}
        showOnlineOffline={false}
        showSyncStatus={false}
        width="640px"
        maxWidth="640px"
        height="auto"
      >
        <div className="vpn-openvpn-root vpn-editor-body">
          <pre className="vpn-file-content">{ovpnConfig}</pre>
        </div>
      </BaseModal>

      <BaseModal
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        title={detailsProfile ? detailsProfile.name : 'OpenVPN Profile'}
        showOnlineOffline={false}
        showSyncStatus={false}
        width="720px"
        maxWidth="720px"
        height="auto"
      >
        {renderDetailsBody()}
      </BaseModal>

      <DeleteWANProfileModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Alert"
        titleWithDeleteIcon={true}
        confirmText="Delete"
        cancelText="Cancel"
        entityLabel={`OpenVPN profile "${deleteTarget?.name}" will be deleted.`}
        entityName={undefined}
        warningMessage={
          deleteTarget?.active
            ? 'This profile is currently active. Deactivate it before deleting.'
            : 'This OpenVPN configuration will be removed from the device.'
        }
      />

      {anyLoading ? <CustomLoader showLoader={true} loadingText={'Please wait...'} /> : null}
    </>
  );
};

const mapStateToProps = (state: any) => ({
  getAllVpnProfiles: state.authReducer.getAllVpnProfiles,
  getAllVpnProfilesLoading: state.authReducer.getAllVpnProfilesLoading,
  getAddEditVpnProfile: state.authReducer.getAddEditVpnProfile,
  getAddEditVpnProfileLoading: state.authReducer.getAddEditVpnProfileLoading,
  getDeleteVpnProfile: state.authReducer.getDeleteVpnProfile,
  getDeleteVpnProfileLoading: state.authReducer.getDeleteVpnProfileLoading,
  setVpnProfileActive: state.authReducer.setVpnProfileActive,
  setVpnProfileActiveLoading: state.authReducer.setVpnProfileActiveLoading,
  getVpnProviderLogin: state.authReducer.getVpnProviderLogin,
  getVpnProviderLoginLoading: state.authReducer.getVpnProviderLoginLoading,
  getVpnProviderCountries: state.authReducer.getVpnProviderCountries,
  getVpnProviderCountriesLoading: state.authReducer.getVpnProviderCountriesLoading,
  getVpnProviderServers: state.authReducer.getVpnProviderServers,
  getVpnProviderServersLoading: state.authReducer.getVpnProviderServersLoading,
  getVpnProviderOvpn: state.authReducer.getVpnProviderOvpn,
  getVpnProviderOvpnLoading: state.authReducer.getVpnProviderOvpnLoading,
  getVpnStatus: state.authReducer.getVpnStatus,
  getVpnStatusLoading: state.authReducer.getVpnStatusLoading,
  getDeviceLinkStatus: state.authReducer.getDeviceLinkStatus,
  deviceLinkStatusLoading: state.authReducer.getDeviceLinkStatusLoading,
});

export default connect(mapStateToProps, {
  getAllVpnProfilesData,
  getAddEditVpnProfileData,
  getDeleteVpnProfileData,
  setVpnProfileActiveData,
  getDeviceLinkStatusData,
  getVpnProviderLoginData,
  getVpnProviderCountriesData,
  getVpnProviderServersData,
  getVpnProviderOvpnData,
  getVpnStatusData,
})(OpenVpnConfig);
